/**
 * Where the organizations settle before anything is drawn.
 *
 * A force simulation pulls linked nodes together and pushes everything else
 * apart, and the resting places it finds are what the layout builds on. It is
 * the one expensive step in the graph, so it is written to run anywhere: the
 * controller sends it to a background worker so the page stays responsive, and
 * runs it in place only if a worker cannot be had.
 *
 * Only the nodes whose places the simulation actually decides are simulated.
 * Somebody who belongs to a single organization is always drawn in a ring
 * around it, wherever the simulation would have left them, so they are left
 * out and their organization pushes a little harder on their behalf.
 */
import { forceCenter, forceLink, forceManyBody, forceSimulation } from "d3-force-3d";
import type { Graph, Point } from "./networkGraph.model";

export type SimulationNode = { id: string; charge: number; x?: number; y?: number };
export type SimulationInput = {
  nodes: SimulationNode[];
  links: { source: string; target: string }[];
};
export type SimulationResult = [id: string, x: number, y: number][];

const ORGANIZATION_CHARGE = -170;
const PERSON_CHARGE = -12;
const LINK_DISTANCE = 34;
const TICKS = 70;

const idOf = (endpoint: string | { id: string }) =>
  typeof endpoint === "string" ? endpoint : endpoint.id;

/** The part of the graph the simulation has to place, with each node's push. */
export function simulationInput(graph: Graph): SimulationInput {
  const type = new Map(graph.nodes.map((node) => [node.id, node.type]));
  const neighbours = new Map<string, string[]>(graph.nodes.map((node) => [node.id, []]));
  for (const link of graph.links) {
    neighbours.get(idOf(link.source))?.push(idOf(link.target));
    neighbours.get(idOf(link.target))?.push(idOf(link.source));
  }

  const ringed = new Set<string>();
  const ringSize = new Map<string, number>();
  for (const node of graph.nodes) {
    const linked = neighbours.get(node.id) ?? [];
    if (node.type === "person" && linked.length === 1 && type.get(linked[0]) === "org") {
      ringed.add(node.id);
      ringSize.set(linked[0], (ringSize.get(linked[0]) ?? 0) + 1);
    }
  }

  return {
    nodes: graph.nodes
      .filter((node) => !ringed.has(node.id))
      .map((node) => ({
        id: node.id,
        charge:
          node.type === "org"
            ? ORGANIZATION_CHARGE + PERSON_CHARGE * (ringSize.get(node.id) ?? 0)
            : PERSON_CHARGE,
      })),
    links: graph.links
      .filter((link) => !ringed.has(idOf(link.source)) && !ringed.has(idOf(link.target)))
      .map((link) => ({ source: idOf(link.source), target: idOf(link.target) })),
  };
}

/** Run the simulation to rest and report where each node ended up. */
export function runSimulation(input: SimulationInput): SimulationResult {
  const nodes = input.nodes.map((node) => ({ ...node }));
  const links = input.links.map((link) => ({ ...link }));
  const simulation = forceSimulation(nodes, 2)
    .force(
      "link",
      forceLink(links)
        .id((node: SimulationNode) => node.id)
        .distance(LINK_DISTANCE)
    )
    .force(
      "charge",
      forceManyBody().strength((node: SimulationNode) => node.charge)
    )
    .force("center", forceCenter())
    .alphaDecay(0.0228)
    .velocityDecay(0.4)
    .stop();
  for (let tick = 0; tick < TICKS; tick += 1) simulation.tick();
  return nodes.map((node) => [node.id, node.x ?? 0, node.y ?? 0]);
}

/**
 * The resting places for a graph, found off the main thread when possible.
 * Nodes left out of the simulation start at their organization.
 */
export async function settle(graph: Graph): Promise<Map<string, Point>> {
  const input = simulationInput(graph);
  const result = await inWorker(input).catch(() => runSimulation(input));
  const placed = new Map<string, Point>(result.map(([id, x, y]) => [id, { x, y }]));
  for (const link of graph.links) {
    const source = idOf(link.source);
    const target = idOf(link.target);
    if (!placed.has(target) && placed.has(source)) placed.set(target, { ...placed.get(source)! });
    if (!placed.has(source) && placed.has(target)) placed.set(source, { ...placed.get(target)! });
  }
  return placed;
}

function inWorker(input: SimulationInput): Promise<SimulationResult> {
  return new Promise((resolve, reject) => {
    if (typeof Worker === "undefined") {
      reject(new Error("No worker available"));
      return;
    }
    const worker = new Worker(new URL("./networkGraph.worker.ts", import.meta.url), {
      type: "module",
    });
    worker.onmessage = (event: MessageEvent<SimulationResult>) => {
      resolve(event.data);
      worker.terminate();
    };
    worker.onerror = (event) => {
      reject(new Error(event.message));
      worker.terminate();
    };
    worker.postMessage(input);
  });
}
